import './PriceHistoryPlot.css';

import React from 'react';

import { CurrencyEnum } from '@search/graphql-typings';
import { formatPrice } from '@search/helpers/src/gql/formatPrice';
import classname from '@search/classname/src';

import { chartTimestampFromDate, useChartSimpleArea } from '../popper/useChart';
import { PriceHistoryDiff } from './PriceHistoryDiff';
import { PriceHistoryPart } from './PriceHistoryPart';

const cn = classname.bind(null, 'PriceHistoryPlot');

export function PriceHistoryPlot({
    history,
    chartProps,
    chartAreaProps
}: {
    history: readonly PriceHistoryPart[];
} & Pick<
    Parameters<typeof useChartSimpleArea>[0],
    'chartProps' | 'chartAreaProps'
>) {
    const { map, items } = React.useMemo(
        () => priceHistoryPlotToChartData(history),
        [ history ]
    );
    const [ activeTime, setActiveTime ] = React.useState(0);
    const selectedPrice = map.get(activeTime);
    const { plotRef, tooltipRef } = useChartSimpleArea({
        items,
        chartProps,
        chartAreaProps,
        setActiveTime
    });

    return (
        <>
            <div className={cn()} ref={plotRef}>
                {' '}
            </div>
            <div className={cn('tooltip')} ref={tooltipRef}>
                <div className={cn('price-diff')}>
                    {selectedPrice?.valueRub && (
                        <span className={cn('price')}>
                            {formatPrice({
                                value: selectedPrice.valueRub,
                                currency: CurrencyEnum.Rub,
                                displayCurrency: true
                            })}
                        </span>
                    )}
                    {selectedPrice?.diff?.rub && (
                        <span className={cn('diff')}>
                            <PriceHistoryDiff diff={selectedPrice.diff.rub} />
                        </span>
                    )}
                </div>
                {selectedPrice?.date2?.formattedFull && (
                    <div className={cn('date')}>
                        {selectedPrice.date2.formattedFull}
                    </div>
                )}
            </div>
        </>
    );
}

function priceHistoryPlotToChartData(history: readonly PriceHistoryPart[]) {
    const map = new Map<number, PriceHistoryPart>();
    const items = history.map(item => {
        const time = chartTimestampFromDate(item.date2?.iso ?? '');

        map.set(time, item);

        return {
            time,
            value: Math.round(item.valueRub ?? 0)
        };
    });

    return {
        map,
        items: items.sort((a, b) => a.time - b.time)
    };
}
